import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import BaseComponent from '../BaseComponent';
import Swiper from 'swiper';
import { Navigation, Pagination, EffectCoverflow } from 'swiper/modules';
import { CAROUSEL_PAGINATION_RESET_EVENT } from '../../webComponents/CarouselPagination';

const NAME = 'MediaCarousel';

const DATA_KEY = 'lta.mediacarousel';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
const SELECTOR_DATA_TOGGLE = '[data-lta-mediacarousel]';

class MediaCarousel extends BaseComponent {
	// eslint-disable-next-line unicorn/no-null
	constructor(element = null) {
		super(element);

		this.componentWrapper_ = element;
		this.swiperState_ = {
			previousSlide: {
				type: undefined,
				videoPlayerComponent: undefined
			}
		};
		this.swiperInstance = undefined;
		this.onPaginationReset_ = this.onPaginationReset_.bind(this);

		if (this.componentWrapper_) {
			this.bindPaginationResetListener_();
			const swiperElement = SelectorEngine.findOne('.swiper', this.componentWrapper_);
			if (!swiperElement) {
				console.error('Swiper element not found in MediaCarousel', this.componentWrapper_);
				return;
			}
			this.initMediaCarousel(swiperElement);
		}
	}

	static get NAME() {
		return NAME;
	}

	bindPaginationResetListener_() {
		this._element.addEventListener(CAROUSEL_PAGINATION_RESET_EVENT, this.onPaginationReset_);
	}

	onPaginationReset_(event) {
		if (event.detail?.variant && event.detail.variant !== 'media-carousel') {
			return;
		}

		this.reinit();
	}

	onSlideChange_(swiper) {
		// Trigger different actions based on previous slide type.
		switch (this.swiperState_.previousSlide.type) {
			case 'video':
				this.onVideoPreviousSlide_();
				break;
			default:
				break;
		}

		// If current slide is type video set the swiper state with the right slide type.
		const videoSlideElement = SelectorEngine.findOne(`.swiper-slide-active[data-is-video]`, swiper.wrapperEl);

		if (videoSlideElement) {
			const videoPlayerComponent = SelectorEngine.findOne('lite-video', videoSlideElement);

			// Resume playback only if we had paused this video when leaving the slide
			if (videoPlayerComponent?.hasAttribute('data-paused-by-carousel')) {
				videoPlayerComponent.removeAttribute('data-paused-by-carousel');
				const playEvent = new CustomEvent('lta-litevideo-event', {
					bubbles: false,
					detail: { command: 'play' }
				});
				videoPlayerComponent.dispatchEvent(playEvent);
			}

			this.swiperState_.previousSlide.type = 'video';
			this.swiperState_.previousSlide.videoPlayerComponent = videoPlayerComponent;

			return;
		}

		// If the current slide is not type video reset to default.
		this.swiperState_.previousSlide.type = 'image';
		this.swiperState_.previousSlide.videoPlayerComponent = undefined;
	}

	onVideoPreviousSlide_() {
		const videoPlayerComponent = this.swiperState_.previousSlide.videoPlayerComponent;
		if (!videoPlayerComponent) {
			return;
		}

		const myEvent = new CustomEvent('lta-litevideo-event', {
			bubbles: false,
			detail: {
				command: 'pause',
			}
		});

		videoPlayerComponent.dispatchEvent(myEvent);
		// Mark so we resume only when returning to this slide (not if user had manually paused)
		videoPlayerComponent.setAttribute('data-paused-by-carousel', 'true');
	}

	initMediaCarousel(swiperElement) {
		// Get the swiper element
		if (!swiperElement) {
			console.error('Swiper element not found in MediaCarousel', this.componentWrapper_);
			return;
		}

		// Get the swiper wrapper
		const swiperWrapper = SelectorEngine.findOne('.swiper-wrapper', swiperElement);
		if (!swiperWrapper) {
			console.error('Swiper wrapper not found in MediaCarousel', swiperElement);
			return;
		}

		const count = swiperWrapper.children.length;		
		
		// Check if loop should be enabled via data attribute on the MediaCarousel wrapper
		// Loop mode breaks pagination, so it's disabled by default
		// Set data-loop="true" on the .lta-media-carousel element to enable it
		const mediaCarouselWrapper = swiperElement.closest('.lta-media-carousel') || this.componentWrapper_;
		const hasEnoughSlidesForLoop = count >= 3;

		const shouldLoop =
			hasEnoughSlidesForLoop &&
			(mediaCarouselWrapper?.hasAttribute('data-loop')
				? mediaCarouselWrapper.getAttribute('data-loop') === 'true'
				: true);
		
		this.swiperInstance = new Swiper(swiperElement, {
			modules: [Navigation, Pagination, EffectCoverflow].filter(Boolean),
			slidesPerView: 'auto',
			centeredSlides: true,
			effect: 'fade',
			coverflowEffect: {
				depth: 200,
				modifier: 1,
				rotate: 0,
				slideShadows: true,
				stretch: -50
			},
			navigation: {
				prevEl: '.lta-media-carousel__prev',
				nextEl: '.lta-media-carousel__next'
			},
			watchSlidesProgress: true,
			grabCursor: true,
			observer: true,
			loop: shouldLoop,
			pagination: {
				el: ".swiper-pagination",
				clickable: true,
				renderBullet(index, className) {
					return `<button class="${className} ${className}${index}" aria-label="Slide ${index+1} of ${count}"></button>`;
				},
			},
			a11y: {
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
				paginationBulletMessage: 'Go to slide {{index}}',
			},
		});

		this.swiperInstance.on('slideChangeTransitionEnd', this.onSlideChange_.bind(this));

		// Sync state from the initial active slide so previousSlide is set before first swipe
		this.onSlideChange_(this.swiperInstance);
	}

	reinit() {
		if (this.swiperInstance) {
			this.swiperInstance.destroy(true, true);
			this.swiperInstance = undefined;
		}

		const swiperElement = SelectorEngine.findOne('.swiper', this.componentWrapper_);

		if (swiperElement) {
			this.initMediaCarousel(swiperElement);
		}
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	MediaCarousel.getInstance(this) || new MediaCarousel(this);
});

export default MediaCarousel;
